// CSS zoom on <html> paints the suite at 67%, but Chromium reports element
// geometry (getBoundingClientRect, offsetWidth, ResizeObserver) in unscaled
// layout pixels while pointer coordinates, the window size and the window
// scroll position stay in visual pixels. Scale those up to layout pixels so
// waveform editors, grids, menus and tooltips keep lining up, and convert
// back for APIs that take visual coordinates (elementsFromPoint, scrollTo).

// Must match the media query that sets --rs-ui-scale in index.css.
export const UI_SCALE_MEDIA = 'screen and (pointer: fine)';

export function parseZoom(raw) {
  if (raw == null) return 1;
  const text = String(raw).trim();
  if (!text || text === 'normal') return 1;
  if (text.endsWith('%')) {
    const percent = parseFloat(text);
    return Number.isFinite(percent) && percent > 0 ? percent / 100 : 1;
  }
  const value = parseFloat(text);
  return Number.isFinite(value) && value > 0 ? value : 1;
}

export function coordinatesNeedBridge(offsetWidth, rectWidth, zoom) {
  if (!Number.isFinite(zoom) || zoom <= 0 || Math.abs(zoom - 1) < 0.001) return false;
  if (!Number.isFinite(offsetWidth) || !Number.isFinite(rectWidth) || offsetWidth <= 0) return false;
  return Math.abs(rectWidth - offsetWidth) < 1;
}

export function scaleScrollArguments(args, zoom) {
  const [first, second] = args;
  if (first !== null && typeof first === 'object') {
    const options = { ...first };
    if (options.left != null) options.left = Number(options.left) * zoom;
    if (options.top != null) options.top = Number(options.top) * zoom;
    return [options];
  }
  if (args.length >= 2) return [Number(first) * zoom, Number(second) * zoom];
  return args;
}

const BRIDGE_FLAG = '__radiosuiteUiScaleBridge';

// The patched APIs read this on every call: the scale follows the pointer
// media query, which flips when a 2-in-1 enters or leaves tablet mode.
let bridgeZoom = 1;
let appliedZoom = 1;
const scaleListeners = new Set();

export function getUiScale() {
  return appliedZoom;
}

export function subscribeUiScale(listener) {
  scaleListeners.add(listener);
  return () => scaleListeners.delete(listener);
}

function setAppliedZoom(zoom) {
  if (zoom === appliedZoom) return;
  appliedZoom = zoom;
  scaleListeners.forEach((listener) => listener());
}

function readAppliedZoom() {
  return parseZoom(getComputedStyle(document.documentElement).zoom);
}

function scaleProperty(target, prop, { appliesTo, scaleWrites = false } = {}) {
  const desc = Object.getOwnPropertyDescriptor(target, prop);
  if (!desc || typeof desc.get !== 'function' || desc.configurable === false) return false;
  const { get: read, set: write } = desc;
  const applies = (self) => !appliesTo || appliesTo(self);
  Object.defineProperty(target, prop, {
    configurable: true,
    enumerable: desc.enumerable,
    get() {
      const value = read.call(this);
      return applies(this) ? value / bridgeZoom : value;
    },
    set: write && scaleWrites
      ? function set(value) {
        write.call(this, applies(this) ? value * bridgeZoom : value);
      }
      : write,
  });
  return true;
}

function scaleScrollMethod(target, name, appliesTo) {
  const original = target[name];
  if (typeof original !== 'function') return;
  target[name] = function scaledScroll(...args) {
    if (appliesTo && !appliesTo(this)) return original.apply(this, args);
    return original.apply(this, scaleScrollArguments(args, bridgeZoom));
  };
}

function installBridge() {
  const patchedClientX = scaleProperty(MouseEvent.prototype, 'clientX')
    && scaleProperty(MouseEvent.prototype, 'clientY');
  if (!patchedClientX) return false;

  ['pageX', 'pageY', 'x', 'y'].forEach((prop) => scaleProperty(MouseEvent.prototype, prop));

  if (typeof Touch !== 'undefined') {
    ['clientX', 'clientY', 'pageX', 'pageY'].forEach((prop) => scaleProperty(Touch.prototype, prop));
  }

  // MUI Popover clamps menus to innerWidth/innerHeight and Popper bounds
  // tooltips by visualViewport and adds the window scroll to element rects.
  ['innerWidth', 'innerHeight', 'scrollX', 'scrollY', 'pageXOffset', 'pageYOffset']
    .forEach((prop) => scaleProperty(window, prop));
  if (typeof VisualViewport !== 'undefined') {
    ['width', 'height', 'offsetLeft', 'offsetTop', 'pageLeft', 'pageTop']
      .forEach((prop) => scaleProperty(VisualViewport.prototype, prop));
  }

  // The root element reports the viewport's size and scroll offsets.
  const isRoot = (element) => element === document.documentElement;
  ['clientWidth', 'clientHeight', 'scrollWidth', 'scrollHeight']
    .forEach((prop) => scaleProperty(Element.prototype, prop, { appliesTo: isRoot }));
  ['scrollTop', 'scrollLeft']
    .forEach((prop) => scaleProperty(Element.prototype, prop, { appliesTo: isRoot, scaleWrites: true }));
  ['scrollTo', 'scroll', 'scrollBy'].forEach((name) => {
    scaleScrollMethod(window, name);
    scaleScrollMethod(Element.prototype, name, isRoot);
  });

  const toVisual = (value) => value * bridgeZoom;
  if (typeof document.elementsFromPoint === 'function') {
    const original = document.elementsFromPoint.bind(document);
    document.elementsFromPoint = (x, y) => original(toVisual(x), toVisual(y));
  }
  if (typeof document.elementFromPoint === 'function') {
    const original = document.elementFromPoint.bind(document);
    document.elementFromPoint = (x, y) => original(toVisual(x), toVisual(y));
  }
  return true;
}

export function installUiScalePointerBridge() {
  if (typeof window === 'undefined' || window[BRIDGE_FLAG]) return;
  window[BRIDGE_FLAG] = true;
  let bridgeInstalled = false;
  let attempts = 0;

  const sync = () => {
    attempts += 1;
    if (!document.body) {
      if (attempts < 3) requestAnimationFrame(sync);
      return;
    }

    const zoom = readAppliedZoom();
    setAppliedZoom(zoom);
    if (Math.abs(zoom - 1) < 0.001) {
      bridgeZoom = 1;
      // The stylesheet may not have applied on the very first check.
      if (attempts < 2) requestAnimationFrame(sync);
      return;
    }

    const probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;left:0;top:0;width:100px;height:1px;visibility:hidden;pointer-events:none';
    document.body.appendChild(probe);
    const offsetWidth = probe.offsetWidth;
    const rectWidth = probe.getBoundingClientRect().width;
    probe.remove();

    if (!coordinatesNeedBridge(offsetWidth, rectWidth, zoom)) {
      bridgeZoom = 1;
      return;
    }
    if (!bridgeInstalled) bridgeInstalled = installBridge();
    bridgeZoom = bridgeInstalled ? zoom : 1;
  };

  sync();

  if (typeof window.matchMedia === 'function') {
    const query = window.matchMedia(UI_SCALE_MEDIA);
    if (typeof query.addEventListener === 'function') query.addEventListener('change', sync);
    else if (typeof query.addListener === 'function') query.addListener(sync);
  }
}
